// BulkEditLabelsModal.tsx — CTSK-498. Palette modal for POST /documents/bulk-edit.
// One row per label field (Group, Collection); each row is
// No change / Set / Clear. Apply stays disabled until a row actually changes.
// The confirmation line (selected count + the pending changes) is shown only
// when the org setting showBulkConfirmationDialogs is on.

import { useEffect, useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BULK_EDIT_STAGE2_FIELDS, normalizeGroupLabel, GROUP_LABEL_MAX_LENGTH } from '@captain-tasko/shared';
import type { DocumentLabelSuggestion } from '@captain-tasko/shared';
import { apiSingle } from '../lib/api.js';
import {
  BulkEditStage2Rows,
  EMPTY_STAGE2,
  buildStage2Mutations,
  describeStage2,
  stage2Errors,
  type Stage2Mutations,
  type Stage2State,
} from './BulkEditStage2Rows.js';

export type LabelMode = 'NO_CHANGE' | 'REPLACE' | 'CLEAR';
export interface LabelRow { mode: LabelMode; value: string }
export interface BulkEditMutations extends Stage2Mutations {
  groupName?: { strategy: 'REPLACE' | 'CLEAR'; value?: string };
  collectionName?: { strategy: 'REPLACE' | 'CLEAR'; value?: string };
}

const EMPTY: LabelRow = { mode: 'NO_CHANGE', value: '' };

/**
 * CTSK-512 - above this many selected documents the modal shows an advisory
 * banner: the edit leaves a large GIN pending list that is flushed in the
 * background, and search for other users can be briefly slower. Advisory only;
 * the server cap (DOCUMENT_BULK_EDIT_MAX_IDS) is unchanged.
 */
export const LARGE_BULK_EDIT_WARNING_THRESHOLD = 500;

/**
 * CTSK-517 - Stage 2 fields (tags, container, custom properties, frontmatter,
 * work-item links) run one transaction per row. Measured on 1,000 rows with all
 * seven fields: ~3.4 s (bulk-edit-perf.ts, Postgres in Docker). So the warning
 * fires much earlier - above this many documents - but ONLY while the pending
 * change includes a Stage 2 field; label-only edits keep the 500 threshold.
 * Advisory only; the server cap is unchanged.
 */
export const LARGE_STAGE2_BULK_EDIT_WARNING_THRESHOLD = 100;

/** Pure: the advisory text for this selection + pending change, or null when none applies. */
export function bulkEditWarning(count: number, mutations: BulkEditMutations): string | null {
  const parts: string[] = [];
  const touchesStage2 = BULK_EDIT_STAGE2_FIELDS.some((f) => mutations[f] !== undefined);
  if (touchesStage2 && count > LARGE_STAGE2_BULK_EDIT_WARNING_THRESHOLD) {
    parts.push('Editing tags, container, custom properties, frontmatter or work-item links on this many documents can take several seconds.');
  }
  if (count > LARGE_BULK_EDIT_WARNING_THRESHOLD) {
    parts.push('Large bulk edits may briefly slow search for other users.');
  }
  return parts.length > 0 ? `${parts.join(' ')} Continue?` : null;
}

/** Pure: rows → request mutations (NO_CHANGE rows and invalid Sets are dropped). */
export function buildMutations(group: LabelRow, collection: LabelRow, stage2: Stage2State = EMPTY_STAGE2): BulkEditMutations {
  const out: BulkEditMutations = { ...buildStage2Mutations(stage2) };
  const one = (row: LabelRow) => {
    if (row.mode === 'CLEAR') return { strategy: 'CLEAR' as const };
    if (row.mode === 'REPLACE') {
      const r = normalizeGroupLabel(row.value);
      if (r.ok && r.value !== null) return { strategy: 'REPLACE' as const, value: r.value };
    }
    return undefined;
  };
  const g = one(group);
  const c = one(collection);
  if (g) out.groupName = g;
  if (c) out.collectionName = c;
  return out;
}

function rowError(row: LabelRow): string | null {
  if (row.mode !== 'REPLACE') return null;
  const r = normalizeGroupLabel(row.value);
  if (!r.ok) return r.message;
  if (r.value === null) return 'Enter a value, or choose Clear.';
  return null;
}

function LabelField({
  label, kind, row, onChange,
}: {
  label: string;
  kind: 'group-names' | 'collection-names';
  row: LabelRow;
  onChange: (r: LabelRow) => void;
}) {
  const listId = useId();
  const name = useId();
  const q = useQuery({
    queryKey: ['documents', 'label-suggestions', kind, row.value.trim()],
    queryFn: () => apiSingle<DocumentLabelSuggestion[]>(`/documents/${kind}`, {
      query: { prefix: row.value.trim() || undefined, limit: 20 },
    }),
    enabled: row.mode === 'REPLACE',
    staleTime: 15_000,
    retry: 0,
  });
  const err = rowError(row);
  return (
    <fieldset style={{ border: 'none', margin: '0 0 var(--space-4) 0', padding: 0 }}>
      <legend style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{label}</legend>
      <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
        {([['NO_CHANGE', 'No change'], ['REPLACE', 'Set'], ['CLEAR', 'Clear']] as const).map(([mode, text]) => (
          <label key={mode} style={{ display: 'inline-flex', gap: 4, fontSize: 13 }}>
            <input
              type="radio"
              name={name}
              checked={row.mode === mode}
              onChange={() => onChange({ ...row, mode })}
            />
            {text}
          </label>
        ))}
      </div>
      {row.mode === 'REPLACE' && (
        <div style={{ marginTop: 6 }}>
          <input
            type="text"
            className="input"
            list={listId}
            aria-label={`${label} value`}
            aria-invalid={err ? true : undefined}
            maxLength={GROUP_LABEL_MAX_LENGTH * 2}
            value={row.value}
            placeholder={`New ${label.toLowerCase()}`}
            onChange={(e) => onChange({ ...row, value: e.target.value })}
            style={{ width: '100%' }}
            autoFocus
          />
          <datalist id={listId}>
            {(q.data ?? []).map((s) => (
              <option key={s.value} value={s.value}>{`${s.value} (${s.count})`}</option>
            ))}
          </datalist>
          {err && <div role="alert" style={{ color: 'var(--danger-primary, #B3261E)', fontSize: 12, marginTop: 4 }}>{err}</div>}
        </div>
      )}
    </fieldset>
  );
}

export function BulkEditLabelsModal({
  open, count, pageScope = false, showConfirmation, isPending, onApply, onCancel,
}: {
  open: boolean;
  count: number;
  /** Selection is limited to the visible page — say so in the confirmation line. */
  pageScope?: boolean;
  showConfirmation: boolean;
  isPending: boolean;
  onApply: (mutations: BulkEditMutations) => void;
  onCancel: () => void;
}) {
  const [group, setGroup] = useState<LabelRow>(EMPTY);
  const [collection, setCollection] = useState<LabelRow>(EMPTY);
  const [stage2, setStage2] = useState<Stage2State>(EMPTY_STAGE2);
  // The warning text the user last said Continue to. A different warning (for example after
  // adding a Stage 2 row to an already-acknowledged large selection) needs a fresh Continue.
  const [ackedWarning, setAckedWarning] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setGroup(EMPTY); setCollection(EMPTY); setStage2(EMPTY_STAGE2); setAckedWarning(null); }
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !isPending) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isPending, onCancel]);

  const mutations = useMemo(() => buildMutations(group, collection, stage2), [group, collection, stage2]);
  const warning = bulkEditWarning(count, mutations);
  const large = warning !== null;
  const largeAck = warning !== null && ackedWarning === warning;
  const hasError = rowError(group) !== null || rowError(collection) !== null || Object.keys(stage2Errors(stage2)).length > 0;
  const canApply = Object.keys(mutations).length > 0 && !hasError && !isPending && count > 0 && (!large || largeAck);

  if (!open) return null;

  const describe = (field: string, m?: { strategy: string; value?: string }) =>
    !m ? null : m.strategy === 'CLEAR' ? `clear ${field}` : `set ${field} to “${m.value}”`;
  const summary = [describe('Group', mutations.groupName), describe('Collection', mutations.collectionName), ...describeStage2(mutations)]
    .filter(Boolean).join(', ');

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Bulk edit documents"
      onClick={() => { if (!isPending) onCancel(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--surface-raised)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', padding: 'var(--space-6)', maxWidth: 560, width: '90%', maxHeight: '90vh', overflowY: 'auto' }}
      >
        <h3 style={{ margin: '0 0 var(--space-4) 0', fontSize: 16 }}>
          Bulk edit {count} document{count === 1 ? '' : 's'}
        </h3>
        <LabelField label="Group" kind="group-names" row={group} onChange={setGroup} />
        <LabelField label="Collection" kind="collection-names" row={collection} onChange={setCollection} />
        <BulkEditStage2Rows value={stage2} onChange={setStage2} />
        {large && (
          <div
            role="alert"
            data-testid="bulk-edit-large-warning"
            style={{ border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', padding: 'var(--space-3)', margin: '0 0 var(--space-4) 0', fontSize: 13 }}
          >
            <div style={{ marginBottom: largeAck ? 0 : 8 }}>
              {warning}
            </div>
            {!largeAck && (
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <button type="button" className="btn" onClick={onCancel} disabled={isPending}>Cancel</button>
                <button type="button" className="btn" onClick={() => setAckedWarning(warning)}>Continue</button>
              </div>
            )}
          </div>
        )}
        {showConfirmation && summary && (
          <p data-testid="bulk-edit-confirmation" style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 var(--space-4) 0' }}>
            This will {summary} on {count} selected document{count === 1 ? '' : 's'}{pageScope ? ' (this page only)' : ''}.
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <button type="button" className="btn" onClick={onCancel} disabled={isPending}>Cancel</button>
          <button type="button" className="btn btn--primary" disabled={!canApply} onClick={() => onApply(mutations)}>
            {isPending ? 'Applying…' : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  );
}
